import { HashIcon, LockIcon, SearchIcon } from 'lucide-react';
import { useState } from 'react';

import { ChoiceList } from '@/modules/signup-flow/components/layout/ChoiceList';
import { buttonClass } from '@/modules/signup-flow/components/layout/motion';
import {
  StatusDot,
  StepActions,
} from '@/modules/signup-flow/components/layout/SignupLayoutParts';
import { StepHeader } from '@/modules/signup-flow/components/layout/StepHeader';
import {
  SIGN_UP_FLOW_STEPS,
  signupFlowStep,
} from '@/modules/signup-flow/signupFlowSteps';
import { rankByPrefix } from '@/modules/slack-notification-channel/channelPages';
import { LoadMoreChannelsError } from '@/modules/slack-notification-channel/components/LoadMoreChannelsError';
import type { SlackChannelApi } from '@/modules/slack-notification-channel/types';
import { Button } from '@/modules/ui/components/button/Button';
import { Input } from '@/modules/ui/components/input/Input';
import { Skeleton } from '@/modules/ui/components/skeleton/Skeleton';

import { PreviewFrame, SlackMessagePreview } from './SlackMessagePreview';
import { SlackTitle } from './SlackTitle';

interface SlackChannelPickerProps {
  /** Slack workspace name from the connected external account. */
  workspaceName: string;
  /** Greets the user in the message preview. */
  firstName?: string;
  channels: SlackChannelApi[];
  isLoadingChannels?: boolean;
  /** The channel list failed to load; the step degrades to "Continue". */
  hasChannelsError?: boolean;
  /** More pages of channels are still loading after the shown ones. */
  isLoadingMore?: boolean;
  /** A later page failed; the shown channels stay usable. */
  hasLoadMoreError?: boolean;
  onRetryLoadMore?: () => void;
  /** Reloads the list from the first page; offered for multi-page workspaces. */
  onRefresh?: () => void;
  isSubmitting?: boolean;
  onBack: () => void;
  onAddToChannel: (channelId: string) => void;
  /** Keeps hiring-manager notifications as direct messages. */
  onSkip: () => void;
}

const SEARCH_THRESHOLD = 6;

const linkClass =
  'text-foreground font-medium underline underline-offset-2 hover:no-underline';

/**
 * Slack lists private channels only once the bot is a member, and the API
 * cannot join them itself (`conversations.join` is public-only).
 */
const PrivateChannelHint = ({ onRefresh }: { onRefresh?: () => void }) => (
  <p className="text-muted-foreground text-[13px] leading-[18px]">
    Private channel missing? Type{' '}
    <code className="text-foreground rounded-sm bg-neutral-100 px-1 py-px text-xs">
      /invite @Finlay
    </code>{' '}
    in it, then{' '}
    {onRefresh ? (
      <button type="button" className={linkClass} onClick={onRefresh}>
        refresh channels
      </button>
    ) : (
      'come back here'
    )}
    .
  </p>
);

const channelCountLabel = (count: number, isLoadingMore: boolean) =>
  isLoadingMore
    ? `${count} channels so far…`
    : count === 1
      ? '1 channel Finlay can join.'
      : `${count} channels Finlay can join.`;

/**
 * Slack step, channel phase (after OAuth): choice rows beside a preview of
 * the message Finlay will post. Selecting a row names the action.
 */
export const SlackChannelPicker = ({
  workspaceName,
  firstName,
  channels,
  isLoadingChannels = false,
  hasChannelsError = false,
  isLoadingMore = false,
  hasLoadMoreError = false,
  onRetryLoadMore,
  onRefresh,
  isSubmitting = false,
  onBack,
  onAddToChannel,
  onSkip,
}: SlackChannelPickerProps) => {
  const [selectedId, setSelectedId] = useState('');
  const [search, setSearch] = useState('');

  const selected = channels.find((channel) => channel.id === selectedId);
  const isEmpty =
    !isLoadingChannels && (hasChannelsError || channels.length === 0);
  const visible = rankByPrefix(channels, search);

  const status = isLoadingChannels
    ? 'Looking for channels…'
    : hasChannelsError
      ? 'We couldn’t load your channels.'
      : channels.length === 0
        ? 'No channel Finlay can join yet.'
        : channelCountLabel(channels.length, isLoadingMore);

  return (
    <div className="flex flex-col gap-8">
      <StepHeader
        step={signupFlowStep.slack}
        totalSteps={SIGN_UP_FLOW_STEPS}
        title={<SlackTitle>Pick a channel in</SlackTitle>}
        subtitle={
          <span className="inline-flex flex-wrap items-center gap-x-3 gap-y-1">
            <StatusDot tone="ok">Connected to {workspaceName}</StatusDot>
            {status}
          </span>
        }
      />

      <div className="grid gap-6 md:grid-cols-[minmax(0,1fr)_300px] md:items-start">
        {isLoadingChannels ? (
          <div
            className="divide-border flex flex-col divide-y border-y"
            aria-busy="true"
            aria-label="Loading channels"
          >
            {[0, 1, 2, 3].map((row) => (
              <div key={row} className="flex items-center gap-3 px-2 py-3.5">
                <Skeleton className="size-4 rounded-full" />
                <Skeleton className="h-4 w-32" />
              </div>
            ))}
          </div>
        ) : isEmpty ? (
          <div className="flex flex-col gap-3">
            <p className="text-muted-foreground border-y py-4 text-[13px]">
              {hasChannelsError
                ? 'You can add Finlay to a channel later from Integrations.'
                : 'Create a channel in Slack, then add Finlay from Integrations.'}{' '}
              Until then, messages go to the Finlay app DM.
            </p>
            {!hasChannelsError && <PrivateChannelHint onRefresh={onRefresh} />}
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {channels.length > SEARCH_THRESHOLD && (
              <label className="relative block">
                <SearchIcon
                  aria-hidden
                  className="text-muted-foreground pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2"
                />
                <Input
                  autoFocus
                  type="search"
                  value={search}
                  aria-label="Search channels"
                  placeholder="Search channels"
                  className="pl-8"
                  onChange={(event) => setSearch(event.target.value)}
                />
              </label>
            )}
            <div className="max-h-[420px] overflow-y-auto [scrollbar-width:thin]">
              <ChoiceList
                name="Slack channel"
                value={selectedId}
                onChange={setSelectedId}
                disabled={isSubmitting}
                stagger
                options={visible.map((channel) => ({
                  value: channel.id,
                  label: channel.name,
                  icon: channel.isPrivate ? LockIcon : HashIcon,
                }))}
              />
            </div>
            {visible.length === 0 && (
              <p className="text-muted-foreground py-2 text-[13px]">
                {/* Search only covers loaded pages; don't call a channel missing yet. */}
                {isLoadingMore
                  ? 'No match yet. Still loading channels…'
                  : `No channels match “${search.trim()}”.`}
              </p>
            )}
            {hasLoadMoreError && onRetryLoadMore && (
              <LoadMoreChannelsError onRetry={onRetryLoadMore} />
            )}
            <PrivateChannelHint onRefresh={onRefresh} />
          </div>
        )}

        <PreviewFrame
          label={
            selected ? 'Finlay will post here' : 'Choose where Finlay posts'
          }
          className="md:sticky md:top-4"
        >
          <SlackMessagePreview
            channelName={selected?.name}
            firstName={firstName}
          />
        </PreviewFrame>
      </div>

      <StepActions>
        <Button
          variant="ghost"
          className={buttonClass}
          disabled={isSubmitting}
          onClick={onBack}
        >
          Back
        </Button>
        {isEmpty ? (
          <Button
            className={buttonClass}
            loading={isSubmitting}
            onClick={onSkip}
          >
            Continue
          </Button>
        ) : (
          <>
            {/* Enabled while channels load: listing can be slow, and the DM
                needs none of them. */}
            <Button
              variant="outline"
              className={buttonClass}
              disabled={isSubmitting}
              onClick={onSkip}
            >
              Use Finlay app DM
            </Button>
            <Button
              className={buttonClass}
              disabled={!selected}
              loading={isSubmitting}
              onClick={() => selected && onAddToChannel(selected.id)}
            >
              {selected
                ? `Add Finlay to #${selected.name}`
                : 'Choose a channel'}
            </Button>
          </>
        )}
      </StepActions>
    </div>
  );
};
